import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import {
  PhotoAlbum as AlbumIcon,
  FormatQuote as QuoteIcon,
  Add as AddIcon,
  Photo as PhotoIcon,
  Videocam as VideoIcon,
  Collections as FeedIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard } from '../../components/common';
import { AlbumFormDialog, QuoteFormDialog, AlbumGalleryDialog } from '../../components/dialogs';
import { ImageViewer, type MediaItem } from '../../components/media';
import { useFamilyData } from '../../hooks';
import { useFamily } from '../../context';
import { getMediaFiles } from '../../services/api/familypal';
import { colors } from '../../theme';

// Normalize file entries from albums (handles both string IDs and typed entries)
const normalizeFileEntry = (entry: any): { id: string; type: 'image' | 'video' } => {
  if (typeof entry === 'string') {
    return { id: entry, type: 'image' };
  }
  return {
    id: entry.id || entry,
    type: entry.type === 'video' ? 'video' : 'image',
  };
};

interface RecentPhoto {
  id: string;
  uri: string;
  thumbnailUri?: string;
  type: 'image' | 'video';
  miniPreview?: string;
}

const MemoriesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { getMemberName } = useFamily();

  const [tab, setTab] = useState(0);

  // Data hooks
  const { items: albums, loading: albumsLoading, insert: insertAlbum, update: updateAlbum, remove: removeAlbum } = useFamilyData('album');
  const { items: quotes, loading: quotesLoading, insert: insertQuote, update: updateQuote, remove: removeQuote } = useFamilyData('quote');

  // Dialog states
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<any>(null);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);

  // Feed state
  const [recentPhotos, setRecentPhotos] = useState<RecentPhoto[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Album covers state (album ID -> cover thumbnail URL)
  const [albumCovers, setAlbumCovers] = useState<Record<string, string>>({});

  const loading = albumsLoading || quotesLoading;

  // Calculate stats
  const stats = useMemo(() => {
    let totalPhotos = 0;
    let totalVideos = 0;

    albums.forEach((album) => {
      const data = album.data?.data || {};
      const entries = data.fileIds || [];
      entries.forEach((entry: any) => {
        const normalized = normalizeFileEntry(entry);
        if (normalized.type === 'video') totalVideos++;
        else totalPhotos++;
      });
    });

    return {
      photos: totalPhotos,
      videos: totalVideos,
      quotes: quotes.length,
      albums: albums.length,
    };
  }, [albums, quotes]);

  // Create stable key for recent photos dependency
  const recentPhotosKey = useMemo(() => {
    return albums.map((a) => `${a.id}:${a.modified_at || ''}`).join(',');
  }, [albums]);

  // Fetch recent photos for feed
  useEffect(() => {
    if (albumsLoading || albums.length === 0) {
      setRecentPhotos([]);
      return;
    }

    const fetchRecentPhotos = async () => {
      setFeedLoading(true);
      try {
        // Collect file IDs from all albums, most recent first
        const allFileIds: { id: string; type: 'image' | 'video' }[] = [];
        const sortedAlbums = [...albums].sort((a, b) =>
          new Date(b.modified_at || 0).getTime() - new Date(a.modified_at || 0).getTime()
        );

        for (const album of sortedAlbums) {
          const entries = album.data?.data?.fileIds || [];
          for (const entry of entries) {
            const normalized = normalizeFileEntry(entry);
            if (!allFileIds.find((f) => f.id === normalized.id)) {
              allFileIds.push(normalized);
            }
            if (allFileIds.length >= 9) break;
          }
          if (allFileIds.length >= 9) break;
        }

        if (allFileIds.length === 0) {
          setRecentPhotos([]);
          return;
        }

        // Fetch media metadata
        const result = await getMediaFiles(allFileIds.map((f) => f.id));
        const files = (result as any)?.files || [];
        const typeMap = new Map(allFileIds.map((f) => [f.id, f.type]));

        const photos: RecentPhoto[] = files.map((file: any) => ({
          id: file.id,
          uri: file.uri || file.url,
          thumbnailUri: file.thumbnailUri || file.thumbnail_url || file.uri || file.url,
          type: typeMap.get(file.id) || 'image',
          miniPreview: file.miniPreview || file.mini_preview,
        }));

        setRecentPhotos(photos);
      } catch (err) {
        console.error('Failed to fetch recent photos:', err);
      } finally {
        setFeedLoading(false);
      }
    };

    fetchRecentPhotos();
  }, [recentPhotosKey, albumsLoading]);

  // Create stable key for album covers dependency
  const albumCoverKey = useMemo(() => {
    return albums.map((a) => {
      const firstFileId = a.data?.data?.fileIds?.[0];
      const normalized = firstFileId ? normalizeFileEntry(firstFileId) : null;
      return `${a.id}:${normalized?.id || ''}`;
    }).join(',');
  }, [albums]);

  // Fetch album covers (first photo of each album)
  useEffect(() => {
    if (albumsLoading || albums.length === 0) {
      setAlbumCovers({});
      return;
    }

    const fetchAlbumCovers = async () => {
      const coverFileIds: { albumId: string; fileId: string }[] = [];

      for (const album of albums) {
        const entries = album.data?.data?.fileIds || [];
        if (entries.length > 0) {
          const firstEntry = normalizeFileEntry(entries[0]);
          coverFileIds.push({ albumId: album.id, fileId: firstEntry.id });
        }
      }

      if (coverFileIds.length === 0) return;

      try {
        const result = await getMediaFiles(coverFileIds.map((c) => c.fileId));
        const files = result?.files || [];
        const fileMap = new Map(files.map((f: any) => [f.id, f.thumbnailUri || f.uri]));

        const covers: Record<string, string> = {};
        for (const { albumId, fileId } of coverFileIds) {
          const url = fileMap.get(fileId);
          if (url) covers[albumId] = url;
        }
        setAlbumCovers(covers);
      } catch (err) {
        console.error('Failed to fetch album covers:', err);
      }
    };

    fetchAlbumCovers();
  }, [albumCoverKey, albumsLoading]);

  const handleAddAction = () => {
    if (tab === 0) {
      // Feed - default to adding album
      setSelectedAlbum(null);
      setAlbumDialogOpen(true);
    } else if (tab === 1) {
      setSelectedAlbum(null);
      setAlbumDialogOpen(true);
    } else {
      setSelectedQuote(null);
      setQuoteDialogOpen(true);
    }
  };

  const handleSaveAlbum = async (data: any, familyIds: string[]) => {
    try {
      if (selectedAlbum?.id) {
        await updateAlbum(selectedAlbum.id, data);
        enqueueSnackbar('Album updated', { variant: 'success' });
      } else {
        // Insert into each selected family
        for (const familyId of familyIds) {
          await insertAlbum(data, familyId);
        }
        enqueueSnackbar('Album created', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save album', { variant: 'error' });
      throw err;
    }
  };

  const handleSaveQuote = async (data: any, familyIds: string[]) => {
    try {
      if (selectedQuote?.id) {
        await updateQuote(selectedQuote.id, data);
        enqueueSnackbar('Quote updated', { variant: 'success' });
      } else {
        // Insert into each selected family
        for (const familyId of familyIds) {
          await insertQuote(data, familyId);
        }
        enqueueSnackbar('Quote added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save quote', { variant: 'error' });
      throw err;
    }
  };

  const handleAlbumClick = (album: any) => {
    setSelectedAlbum(album);
    setGalleryDialogOpen(true);
  };

  const handleEditAlbum = () => {
    setGalleryDialogOpen(false);
    setAlbumDialogOpen(true);
  };

  const handlePhotoClick = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // Get latest quote for feed
  const latestQuote = useMemo(() => {
    if (quotes.length === 0) return null;
    const sorted = [...quotes].sort((a, b) =>
      new Date(b.modified_at || 0).getTime() - new Date(a.modified_at || 0).getTime()
    );
    return sorted[0];
  }, [quotes]);

  if (loading) {
    return <LoadingSpinner message="Loading memories..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Scrapbook"
        subtitle="Photos, albums, and memorable quotes"
        actionLabel={tab === 2 ? 'Add Quote' : 'Create Album'}
        actionIcon={AddIcon}
        onAction={handleAddAction}
      />

      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid size={{ xs: 6, sm: 3 }}>
          <DataCard
            title="Photos"
            icon={PhotoIcon}
            iconColor={colors.memories}
            value={stats.photos}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <DataCard
            title="Videos"
            icon={VideoIcon}
            iconColor="#E91E63"
            value={stats.videos}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <DataCard
            title="Albums"
            icon={AlbumIcon}
            iconColor={colors.memories}
            value={stats.albums}
            onClick={() => setTab(1)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <DataCard
            title="Quotes"
            icon={QuoteIcon}
            iconColor="#9C27B0"
            value={stats.quotes}
            onClick={() => setTab(2)}
          />
        </Grid>

        <Grid size={12}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab icon={<FeedIcon />} iconPosition="start" label="Feed" />
                  <Tab icon={<AlbumIcon />} iconPosition="start" label={`Albums (${albums.length})`} />
                  <Tab icon={<QuoteIcon />} iconPosition="start" label={`Quotes (${quotes.length})`} />
                </Tabs>
              </Box>

              {/* Feed Tab */}
              {tab === 0 && (
                <Box>
                  {/* Recent Photos Grid */}
                  <Typography variant="h6" gutterBottom>Recent Photos</Typography>
                  {feedLoading ? (
                    <Grid container spacing={1} sx={{ mb: 4 }}>
                      {[...Array(6)].map((_, i) => (
                        <Grid size={{ xs: 4, sm: 2 }} key={i}>
                          <Skeleton variant="rectangular" sx={{ aspectRatio: '1', borderRadius: 1 }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : recentPhotos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, mb: 4 }}>
                      <PhotoIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                      <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No photos yet. Create an album to get started.
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1} sx={{ mb: 4 }}>
                      {recentPhotos.slice(0, 6).map((photo, index) => (
                        <Grid size={{ xs: 4, sm: 2 }} key={photo.id}>
                          <Card
                            sx={{
                              aspectRatio: '1',
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': { opacity: 0.9 },
                            }}
                            onClick={() => handlePhotoClick(index)}
                          >
                            <CardMedia
                              component="img"
                              image={photo.thumbnailUri || photo.uri}
                              alt=""
                              sx={{
                                width: '100%', height: '100%', objectFit: 'cover',
                                backgroundImage: photo.miniPreview ? `url(data:image/jpeg;base64,${photo.miniPreview})` : undefined,
                                backgroundSize: 'cover',
                              }}
                            />
                            {photo.type === 'video' && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  right: 4,
                                  bgcolor: 'rgba(0,0,0,0.6)',
                                  borderRadius: '50%',
                                  p: 0.5,
                                  display: 'flex',
                                }}
                              >
                                <VideoIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Latest Quote */}
                  {latestQuote && (
                    <>
                      <Typography variant="h6" gutterBottom>Latest Quote</Typography>
                      <Card
                        variant="outlined"
                        sx={{ mb: 4, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => {
                          setSelectedQuote(latestQuote);
                          setQuoteDialogOpen(true);
                        }}
                      >
                        <CardContent>
                          <QuoteIcon sx={{ color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1 }}>
                            "{latestQuote.data?.data?.text || 'No quote text'}"
                          </Typography>
                          {latestQuote.data?.data?.personId && (
                            <Typography variant="body2" color="text.secondary" align="right">
                              — {getMemberName(latestQuote.data.data.personId)}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}

                  {/* Albums Preview */}
                  <Typography variant="h6" gutterBottom>Albums</Typography>
                  {albums.length === 0 ? (
                    <EmptyState
                      icon={AlbumIcon}
                      title="No albums yet"
                      description="Create albums to organize your family photos"
                      actionLabel="Create Album"
                      onAction={() => {
                        setSelectedAlbum(null);
                        setAlbumDialogOpen(true);
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                      {albums.slice(0, 6).map((album) => {
                        const data = album.data?.data || {};
                        const entries = data.fileIds || [];
                        const photoCount = entries.filter((e: any) => normalizeFileEntry(e).type === 'image').length;
                        const videoCount = entries.filter((e: any) => normalizeFileEntry(e).type === 'video').length;

                        return (
                          <Card
                            key={album.id}
                            sx={{
                              minWidth: 140,
                              cursor: 'pointer',
                              '&:hover': { boxShadow: 4 },
                            }}
                            onClick={() => handleAlbumClick(album)}
                          >
                            {albumCovers[album.id] ? (
                              <CardMedia
                                component="img"
                                image={albumCovers[album.id]}
                                sx={{ height: 100, objectFit: 'cover' }}
                              />
                            ) : (
                              <CardMedia
                                component="div"
                                sx={{
                                  height: 100,
                                  bgcolor: colors.memories + '30',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <AlbumIcon sx={{ fontSize: 40, color: colors.memories }} />
                              </CardMedia>
                            )}
                            <CardContent sx={{ py: 1 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {data.name || 'Untitled'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {photoCount + videoCount} items
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Albums Tab */}
              {tab === 1 && (
                albums.length === 0 ? (
                  <EmptyState
                    icon={AlbumIcon}
                    title="No albums yet"
                    description="Create albums to organize your family photos"
                    actionLabel="Create Album"
                    onAction={() => {
                      setSelectedAlbum(null);
                      setAlbumDialogOpen(true);
                    }}
                  />
                ) : (
                  <Grid container spacing={2}>
                    {albums.map((album) => {
                      const data = (album.data?.data || {}) as any;
                      const entries = data.fileIds || [];
                      let photoCount = 0;
                      let videoCount = 0;
                      entries.forEach((entry: any) => {
                        const type = typeof entry === 'string' ? 'image' : entry.type;
                        if (type === 'video') videoCount++;
                        else photoCount++;
                      });
                      return (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={album.id}>
                          <Card
                            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                            onClick={() => handleAlbumClick(album)}
                          >
                            {albumCovers[album.id] ? (
                              <CardMedia
                                component="img"
                                image={albumCovers[album.id]}
                                sx={{ height: 140, objectFit: 'cover' }}
                              />
                            ) : (
                              <CardMedia
                                component="div"
                                sx={{
                                  height: 140,
                                  bgcolor: colors.memories + '30',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <AlbumIcon sx={{ fontSize: 48, color: colors.memories }} />
                              </CardMedia>
                            )}
                            <CardContent sx={{ py: 1.5 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {data.name || 'Untitled Album'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                                {photoCount > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PhotoIcon fontSize="inherit" /> {photoCount}
                                  </Typography>
                                )}
                                {videoCount > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <VideoIcon fontSize="inherit" /> {videoCount}
                                  </Typography>
                                )}
                                {photoCount === 0 && videoCount === 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Empty album
                                  </Typography>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )
              )}

              {/* Quotes Tab */}
              {tab === 2 && (
                quotes.length === 0 ? (
                  <EmptyState
                    icon={QuoteIcon}
                    title="No quotes yet"
                    description="Save memorable things your family members say"
                    actionLabel="Add Quote"
                    onAction={() => {
                      setSelectedQuote(null);
                      setQuoteDialogOpen(true);
                    }}
                  />
                ) : (
                  <Grid container spacing={2}>
                    {quotes.map((quote) => {
                      const data = (quote.data?.data || {}) as any;
                      const authorName = data.personId ? getMemberName(data.personId) : data.author;
                      return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={quote.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                              borderLeft: data.color ? `4px solid ${data.color}` : undefined,
                            }}
                            onClick={() => {
                              setSelectedQuote(quote);
                              setQuoteDialogOpen(true);
                            }}
                          >
                            <CardContent>
                              <QuoteIcon sx={{ color: data.color || 'text.secondary', mb: 1 }} />
                              <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                                "{data.text || 'No quote text'}"
                              </Typography>
                              {authorName && (
                                <Typography variant="body2" color="text.secondary" align="right">
                                  — {authorName}{(data.authorAge || data.author_age) ? `, age ${data.authorAge || data.author_age}` : ''}
                                </Typography>
                              )}
                              {data.date && (
                                <Typography variant="caption" color="text.secondary" display="block" align="right">
                                  {data.date}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <AlbumFormDialog
        open={albumDialogOpen}
        onClose={() => { setAlbumDialogOpen(false); setSelectedAlbum(null); }}
        onSave={handleSaveAlbum}
        onDelete={selectedAlbum ? async () => {
          try { await removeAlbum(selectedAlbum.id); enqueueSnackbar('Album deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete album', { variant: 'error' }); throw err; }
        } : undefined}
        album={selectedAlbum}
      />

      <QuoteFormDialog
        open={quoteDialogOpen}
        onClose={() => { setQuoteDialogOpen(false); setSelectedQuote(null); }}
        onSave={handleSaveQuote}
        onDelete={selectedQuote ? async () => {
          try { await removeQuote(selectedQuote.id); enqueueSnackbar('Quote deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete quote', { variant: 'error' }); throw err; }
        } : undefined}
        quote={selectedQuote}
      />

      <AlbumGalleryDialog
        open={galleryDialogOpen}
        onClose={() => { setGalleryDialogOpen(false); setSelectedAlbum(null); }}
        onEdit={handleEditAlbum}
        onDelete={selectedAlbum ? async () => {
          try {
            await removeAlbum(selectedAlbum.id);
            setGalleryDialogOpen(false);
            setSelectedAlbum(null);
            enqueueSnackbar('Album deleted', { variant: 'success' });
          }
          catch (err) { enqueueSnackbar('Failed to delete album', { variant: 'error' }); throw err; }
        } : undefined}
        album={selectedAlbum}
      />

      {/* Image Viewer for Feed photos */}
      <ImageViewer
        items={recentPhotos}
        initialIndex={viewerIndex}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
    </Box>
  );
};

export default MemoriesPage;
